import { useMutation, useQuery } from "@tanstack/react-query";
import * as api from "./request";

export function useGetAuditorLimeStatus(userId: string) {
    const { data, isFetching } = useQuery(
        ['auditorLimeStatus', userId],
        async () => await api.get(`/api/Auditor/GetAuditorDashboard?auditorId=${userId}`),
        {
            refetchOnMount: false,
            refetchOnWindowFocus: false,
            enabled: !!userId
        }
    );

    return { limeStatus: (data || {}).data || {}, isFetching };
}

export function useGetAuditorPerformance(userId: string, frequency: any) {
    const { data, isFetching } = useQuery(
        ['auditorPerformance', userId, frequency],
        async () => await api.get(`/api/Auditor/GetPerformance?auditorId=${userId}&frequency=${frequency}`),
        {
            refetchOnMount: false,
            refetchOnWindowFocus: false,
            enabled: !!userId && typeof frequency !== 'undefined'
        }
    );

    return { auditorPerformance: (data || {}).data || {}, isFetching };
}

export function useGetAuditorActivites(payload: any) {
    const { data, isFetching, refetch } = useQuery(
        ['auditorActivities', payload],
        async () => await api.post('/api/ToDo/GetToDoByCriteria', payload),
        {
            refetchOnMount: false,
            refetchOnWindowFocus: false,
            enabled: !!payload
        }
    );

    return { activities: (data || {}).data || [], isFetching, refetch };
}

export function useGetLocationAuditScoreCards(payload: any) {
    const { data, isFetching, refetch } = useQuery(
        ['locationAuditScoreCards', payload],
        async () => await api.get('/api/v1/location_audits/score_cards_counts', payload),
        {
            refetchOnMount: false,
            refetchOnWindowFocus: false,
            enabled: !!(payload && payload.company_id),
        }
    );
    const result = ((data as any) && (data as any).data) || {};
    return {
        scoreCards: result as {
            avg_overall_score?: number;
            avg_critical_score?: number;
            active_locations?: number;
            total_audits_ytd?: number;
            due_audits_count?: number;
        },
        isFetching,
        refetch,
    };
}

export function useDownloadLocationAuditDashboardReport(onSuccess?: any, onError?: any) {
    const { mutate: downloadDashboardReport, error, isLoading: downloadingReport } = useMutation(
        ['downloadLocationAuditDashboardReport'],
        async (payload: any) => await api.get('/api/v1/location_audits/download_dashboard_report.pdf', payload, null, { responseType: 'blob' }),
        {
            onError,
            onSuccess: (response: any) => {
                onSuccess(response || {});
            }
        }
    );
    return { downloadDashboardReport, error, downloadingReport };
}

export function useGetLocationAuditExecutionStatus(payload: any) {
    const { data, isFetching, refetch } = useQuery(
        ['locationAuditExecutionStatus', payload],
        async () => await api.get('/api/v1/location_audits/audit_execution_status_count', payload),
        {
            refetchOnMount: false,
            refetchOnWindowFocus: false,
            enabled: !!(payload && payload.company_id),
        }
    );
    const result = ((data as any) && (data as any).data) || {};
    return {
        executionStatus: result as {
            scheduled_count?: number;
            preliminary_in_progress_count?: number;
            preliminary_completed_count?: number;
            final_completed_count?: number;
            dropped_audits_count?: number;
            total_count?: number;
        },
        isFetching,
        refetch,
    };
}

export function useGetLocationAuditPerformanceMatrix(payload: any) {
    const { data, isFetching, refetch } = useQuery(
        ['locationAuditPerformanceMatrix', payload],
        async () => await api.get('/api/v1/location_audits/vendor_performance_matrix', payload),
        {
            refetchOnMount: false,
            refetchOnWindowFocus: false,
            enabled: !!(payload && payload.company_id),
            keepPreviousData: true,
            staleTime: 30000,
        }
    );
    const raw = ((data as any) && (data as any).data) || null;
    const rows: Array<{
        location_id: string;
        location_name: string;
        location_code?: string;
        monthly_scores: Array<{
            month: string;
            year: string;
            overall_score: string | null;
            critical_score: string | null;
            audit_id?: string | null;
        }>;
        ytd_overall_score?: string | null;
        ytd_critical_score?: string | null;
    }> = Array.isArray(raw) ? raw : ((raw && Array.isArray(raw.data)) ? raw.data : []);
    return { matrixRows: rows, isFetching, refetch };
}

export function useGetLocationAuditComplianceItemStatus(payload: any) {
    const { data, isFetching, refetch } = useQuery(
        ['locationAuditComplianceItemStatus', payload],
        async () => await api.get('/api/v1/location_audits/compliance_item_status_count', payload),
        {
            refetchOnMount: false,
            refetchOnWindowFocus: false,
            enabled: !!(payload && payload.company_id),
        }
    );
    const result = ((data as any) && (data as any).data) || {};
    return {
        complianceItemStatus: result as {
            audited_count?: number;
            submitted_count?: number;
            pending_count?: number;
            overdue_count?: number;
            rejected_count?: number;
            dropped_count?: number;
            total_count?: number;
        },
        isFetching,
        refetch,
    };
}

export function useSendReportAsEmail(onSuccess?: any, onError?: any) {
    const { mutate: sendEmail, error, isLoading: adding } = useMutation(
        ['sendEmail'],
        async (payload: any) => await api.post('/api/Auditor/SendEmailDownlaodReport', payload),
        {
            onError,
            onSuccess: (response) => {
                const { data } = response || {};
                onSuccess(data || {});
            }
        }
    );
    return { sendEmail, error, adding };
}